import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { HardHat, ArrowRight } from "lucide-react";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) {
      // Native `required` on the checkbox handles the UX; this is a belt-
      // and-suspenders guard in case a keyboard user bypasses it.
      toast.error("Please agree to the Terms of Use and Privacy Policy.");
      return;
    }
    setBusy(true);
    const r = await register(name, email, password);
    setBusy(false);
    if (!r.ok) { toast.error(r.error); return; }
    toast.success("Account created");
    nav("/onboarding");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1F1F1F] bi-grain px-4">
      <div className="w-full max-w-md relative z-10">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-lg bg-[#FF9500] flex items-center justify-center text-[#111827]"><HardHat className="w-6 h-6" /></div>
          <div>
            <div className="font-heading font-extrabold text-white text-2xl tracking-tight leading-none">BUILDINTEL <span className="text-[#FF9500]">AI</span></div>
            <div className="text-xs text-white/50 mt-1">Built for Aussie tradies</div>
          </div>
        </div>
        <div className="bi-surface p-6 sm:p-8">
          <h1 className="font-heading font-bold text-2xl">Create your account</h1>
          <p className="text-white/60 text-sm mt-1">Snap it. Speak it. Done.</p>

          <GoogleSignInButton testid="register-google-btn" label="Sign up with Google" />
          <div className="flex items-center gap-3 my-5">
            <div className="h-px bg-white/10 flex-1" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-heading">or use email</span>
            <div className="h-px bg-white/10 flex-1" />
          </div>

          <form onSubmit={onSubmit} className="mt-1 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-white/60">Your name</label>
              <input data-testid="reg-name" required autoComplete="name" autoCapitalize="words" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full bi-input" placeholder="Bluey Carpenter"/>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/60">Email</label>
              <input data-testid="reg-email" type="email" required autoComplete="email" inputMode="email" autoCapitalize="none" spellCheck={false} value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full bi-input" placeholder="you@trade.com.au"/>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/60">Password</label>
              <input data-testid="reg-password" type="password" required minLength={6} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full bi-input" placeholder="At least 6 characters"/>
            </div>
            <label
              data-testid="reg-terms-label"
              className="flex items-start gap-2.5 cursor-pointer select-none pt-1"
            >
              <input
                data-testid="reg-terms-checkbox"
                type="checkbox"
                required
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#FF9500] shrink-0"
              />
              <span className="text-[13px] text-white/75 leading-snug">
                I agree to the{" "}
                <Link
                  to="/terms"
                  target="_blank"
                  rel="noopener"
                  data-testid="reg-terms-link"
                  className="text-[#FF9500] hover:underline"
                >
                  Terms of Use
                </Link>{" "}
                and{" "}
                <Link
                  to="/privacy"
                  target="_blank"
                  rel="noopener"
                  data-testid="reg-privacy-link"
                  className="text-[#FF9500] hover:underline"
                >
                  Privacy Policy
                </Link>
                .
              </span>
            </label>
            <button data-testid="reg-submit" type="submit" disabled={busy || !agreed} className="w-full h-14 rounded-xl bi-accent-btn text-base flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
              {busy ? "Creating…" : "Create account"} <ArrowRight className="w-5 h-5" />
            </button>
          </form>
          <div className="mt-4 text-center text-sm text-white/60">
            Already have an account? <Link data-testid="go-login" to="/login" className="text-[#FF9500] font-semibold">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
